/* ============================================================
   components.jsx — shared UI: Logo, Header, Avatar, ImageCard
   ============================================================ */

import CONFIG from "./config/config.js";
import api from "./api/api.js";

/* ---- scroll-reveal: CSS fade-up on mount (no JS dependency; always settles visible) ---- */
function Reveal({ children, delay = 0, className = "", style = {} }) {
  const ref = React.useRef(null);
  // Safety net: if the entrance animation never runs (e.g. preview tab
  // backgrounded → animation timeline paused at the hidden start frame),
  // force the visible end-state so content is never stuck invisible.
  React.useEffect(() => {
    const el = ref.current;
    const id = setTimeout(() => { if (el) el.classList.add("rv-on"); }, 900);
    return () => clearTimeout(id);
  }, []);
  return (
    <div ref={ref} className={"reveal " + className} style={{ animationDelay: delay + "ms", ...style }}>
      {children}
    </div>
  );
}

function Logo({ size = 22, onClick, theme }) {
  const isDark = theme === "dark";
  return (
    <button onClick={onClick} aria-label="Promzi 홈" style={{
      display: "inline-flex", alignItems: "center", gap: 9, background: "none", padding: 0,
    }}>
      {isDark ? (
        <>
          <img src="/brand/app_logo_dark.png" alt="" style={{ width: size + 8, height: size + 8, borderRadius: 9, display: "block" }} />
          <img src="/brand/text_logo_dark.png" alt="Promzi" style={{ height: size + 6, width: "auto", display: "block" }} />
        </>
      ) : (
        <>
          <img src="/brand/app_logo_light.png" alt="" style={{ width: size + 8, height: size + 8, borderRadius: 9, display: "block" }} />
          <img src="/brand/text_logo_light.png" alt="Promzi" style={{ height: size + 6, width: "auto", display: "block" }} />
        </>
      )}
    </button>
  );
}

function Avatar({ author, size = 32, ring = false }) {
  const nm = author ? displayUserName(author) : null;
  let initials = "ME";
  if (nm) {
    initials = nm.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%", flex: "none",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: "var(--k-yellow)", color: "#000", fontFamily: "var(--font-disp)",
      fontWeight: 700, fontSize: size * 0.36, letterSpacing: "-0.02em",
      boxShadow: ring ? "0 0 0 2px var(--paper), 0 0 0 3.5px #000" : "none",
    }}>{initials}</span>
  );
}

function RemoteImageLogoPlaceholder({ ratio = "1 / 1", radius = "var(--r-md)", style = {}, children }) {
  return (
    <div className="remote-image-placeholder" style={{ position: "relative", aspectRatio: ratio, borderRadius: radius, overflow: "hidden", ...style }}>
      <div className="remote-image-placeholder__inner" aria-hidden="true">
        <img className="remote-image-placeholder__logo remote-image-placeholder__logo--light" src="/brand/app_logo_light.png" alt="" />
        <img className="remote-image-placeholder__logo remote-image-placeholder__logo--dark" src="/brand/app_logo_dark.png" alt="" />
      </div>
      {children}
    </div>
  );
}

function RemoteImageSkeletonPlaceholder({ ratio = "1 / 1", radius = "var(--r-md)", style = {}, children }) {
  return (
    <div className="shimmer" style={{ position: "relative", aspectRatio: ratio, borderRadius: radius, overflow: "hidden", background: "var(--surface-3)", ...style }}>
      {children}
    </div>
  );
}

function displayUserName(user) {
  if (!user) return "";
  return user.displayName || user.name || user.handle || user.email || "";
}

/* localized display name for an author/user */
function authorName(author, lang) {
  if (!author) return "";
  return (lang === "en" && author.nameEn ? author.nameEn : author.name) || author.displayName || author.handle || "";
}

function RemoteImage({ item, ratio = "1 / 1", radius = "var(--r-md)", style = {}, children, resolution = "std", fit = "cover", loadingPlaceholder = "logo" }) {
  const [url, setUrl] = React.useState(item.url || item.thumbnailUrl || "");
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setUrl(item.url || item.thumbnailUrl || "");
    setFailed(false);
    if (CONFIG.USE_MOCK || item.url || item.thumbnailUrl || !item.id) return () => { cancelled = true; };
    api.images.download(item.id, { resolution }).then((res) => {
      if (!cancelled && res?.url && res.url !== "about:blank") setUrl(res.url);
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => { cancelled = true; };
  }, [item.id, item.url, item.thumbnailUrl, resolution]);

  if (!url) {
    const Placeholder = loadingPlaceholder === "skeleton" ? RemoteImageSkeletonPlaceholder : RemoteImageLogoPlaceholder;
    return <Placeholder ratio={ratio} radius={radius} style={style}>{children}</Placeholder>;
  }

  if (failed) {
    return <ArtImage seed={item.seed} palette={item.palette} ratio={ratio} radius={radius} style={style}>{children}</ArtImage>;
  }

  return (
    <div className="art-img" style={{ position: "relative", aspectRatio: ratio, borderRadius: radius, overflow: "hidden", background: "var(--surface-3)", ...style }}>
      <img src={url} alt="" onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }} />
      {children}
    </div>
  );
}

/* ---------- Top navigation ---------- */
function Header({ route, go, open, auth, onLogin, onLogout, q, setQ, lang, setLang, theme, setTheme, toast, items = [] }) {
  const { t } = useT();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [acctOpen, setAcctOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchDraft, setSearchDraft] = React.useState(q);
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => normalizeSearchQuery(q));
  const searchInputRef = React.useRef(null);
  React.useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [menuOpen, searchOpen]);
  React.useEffect(() => {
    if (searchOpen) return;
    setSearchDraft(q);
  }, [q, searchOpen]);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(normalizeSearchQuery(searchDraft)), 400);
    return () => window.clearTimeout(id);
  }, [searchDraft]);
  React.useEffect(() => {
    if (!searchOpen) return undefined;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 10);
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeSearch();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [searchOpen, closeSearch]);
  const nav = [
    CONFIG.FEATURES.generation ? { id: "generate", label: t("nav_generate") } : null,
    { id: "mypage", label: t("nav_mypage") },
  ].filter(Boolean);
  const isActive = (id) => route === id || (id === "gallery" && route === "detail");
  const langToggle = (cls) => (
    <div className={"lang-seg " + cls} role="group" aria-label="Language">
      <button data-on={lang === "ko"} onClick={() => setLang("ko")}>KOR</button>
      <button data-on={lang === "en"} onClick={() => setLang("en")}>ENG</button>
    </div>
  );
  const themeToggle = (cls) => (
    <div className={"lang-seg " + cls} role="group" aria-label="Theme">
      <button data-on={theme === "light"} onClick={() => setTheme("light")}>Light</button>
      <button data-on={theme === "dark"} onClick={() => setTheme("dark")}>Dark</button>
    </div>
  );
  const filteredResults = React.useMemo(() => {
    const terms = splitSearchTerms(debouncedSearch);
    if (!terms.length) return [];
    return (items || []).filter((it) => {
      const promptKo = (it.prompt || "").toLowerCase();
      const promptEn = (it.promptEn || "").toLowerCase();
      const authorKo = (it.author?.name || "").toLowerCase();
      const authorEn = (it.author?.nameEn || "").toLowerCase();
      const handle = (it.author?.handle || "").toLowerCase();
      const tags = (it.tags || []).map((tg) => tg.toLowerCase());
      const tagsEn = (it.tags || []).map((tg) => (TAG_EN[tg] || "").toLowerCase());
      return terms.some((term) => (
        promptKo.includes(term)
        || promptEn.includes(term)
        || authorKo.includes(term)
        || authorEn.includes(term)
        || handle.includes(term)
        || tags.some((tg) => tg.includes(term))
        || tagsEn.some((tg) => tg.includes(term))
      ));
    }).slice(0, 8);
  }, [debouncedSearch, items]);
  function openSearch() {
    setAcctOpen(false);
    setMenuOpen(false);
    setSearchDraft(q);
    setSearchOpen(true);
  }
  function closeSearch() {
    setSearchOpen(false);
    setSearchDraft("");
    setDebouncedSearch("");
    setQ("");
  }
  function clearSearchInput() {
    setSearchDraft("");
    setDebouncedSearch("");
    setQ("");
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }
  const submitSearch = React.useCallback((event) => {
    event?.preventDefault?.();
    const nextQ = normalizeSearchQuery(searchDraft);
    setSearchDraft(nextQ);
    setDebouncedSearch(nextQ);
    setQ(nextQ);
    setSearchOpen(false);
    if (route !== "gallery") go("gallery");
  }, [go, route, searchDraft, setQ]);
  const selectSearchResult = React.useCallback((item) => {
    const nextQ = normalizeSearchQuery(searchDraft);
    setSearchDraft(nextQ);
    setDebouncedSearch(nextQ);
    setQ(nextQ);
    setSearchOpen(false);
    open(item);
  }, [open, searchDraft, setQ]);

  return (
    <>
      <header style={{
        position: "sticky", top: 0, zIndex: 50, background: "color-mix(in srgb, var(--k-bg) 88%, transparent)",
        backdropFilter: "saturate(180%) blur(14px)", WebkitBackdropFilter: "saturate(180%) blur(14px)",
        borderBottom: "1px solid var(--line)",
      }}>
        <div className="container" style={{ height: 64, display: "flex", alignItems: "center", gap: 18 }}>
        <Logo onClick={() => go("gallery")} theme={theme} />

        <nav style={{ display: "flex", alignItems: "center", gap: 2 }} className="nav-desktop">
          {nav.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} style={{
              height: 38, padding: "0 14px", borderRadius: "var(--r-pill)",
              fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em",
              color: isActive(n.id) ? "var(--ink)" : "var(--ink-3)",
              background: isActive(n.id) ? "var(--surface-3)" : "transparent",
              transition: "all .15s var(--ease)",
            }}>{n.label}</button>
          ))}
        </nav>

        {/* search */}
        <button className="nav-search" onClick={openSearch} aria-label={t("search_ph")} style={{
          marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
          height: 42, padding: "0 14px", minWidth: 220, maxWidth: 320, flex: "1 1 240px",
          background: "var(--surface-2)", border: "1px solid var(--line-2)", borderRadius: "var(--r-pill)",
          color: q.trim() ? "var(--ink)" : "var(--ink-4)", textAlign: "left",
        }}>
          <Icon name="search" size={18} style={{ color: "var(--ink-4)" }} />
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontFamily: "inherit" }}>
            {normalizeSearchQuery(q) || t("search_ph")}
          </span>
        </button>


        {auth ? (
          <div style={{ position: "relative" }} className="nav-desktop">
            <button onClick={() => setAcctOpen((v) => !v)} aria-label={t("account")}>
              <Avatar author={auth} size={38} />
            </button>
            {acctOpen && (
              <>
                <div onClick={() => setAcctOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1 }} />
                <div className="card" style={{
                  position: "absolute", right: 0, top: 46, width: 220, zIndex: 2, padding: 8,
                  boxShadow: "var(--sh-3)", borderRadius: "var(--r-md)",
                }}>
                  <div style={{ padding: "8px 10px 10px", display: "flex", gap: 10, alignItems: "center" }}>
                    <Avatar author={auth} size={38} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{displayUserName(auth)}</div>
                      <div className="muted" style={{ fontSize: 12 }}>@{auth.handle || "me"}</div>
                    </div>
                  </div>
                  <hr className="divider" style={{ margin: "4px 0" }} />
                  {[
                    ["folder", t("menu_mywork"), () => go({ name: "mypage", tab: "created" })],
                    ["bookmark", t("menu_saved"), () => go({ name: "mypage", tab: "saved" })],
                    ["image", t("g_upload"), () => go({ name: "upload" })],
                    ["settings", t("menu_settings"), () => go({ name: "settings" })],
                  ].map(([ic, lb, action]) => (
                    <button key={lb} onClick={() => { setAcctOpen(false); action(); }} style={menuItemStyle}>
                      <Icon name={ic} size={18} style={{ color: "var(--ink-3)" }} /> {lb}
                    </button>
                  ))}
                  <hr className="divider" style={{ margin: "4px 0" }} />
                  <button onClick={() => { setAcctOpen(false); onLogout(); }} style={{ ...menuItemStyle, color: "var(--ink-3)" }}>
                    {t("logout")}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <button className="btn btn-line btn-sm nav-desktop" onClick={onLogin}>{t("login")}</button>
        )}

        {/* mobile menu trigger */}
        <button className="btn-icon nav-mobile" onClick={() => setMenuOpen(true)} aria-label={t("menu")}
          style={{ background: "var(--surface-2)", border: "1px solid var(--line-2)" }}>
          <Icon name="menu" size={20} />
        </button>
        </div>
      </header>

      {searchOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 140, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "min(30vh, 220px) 16px 24px" }}>
          <div onClick={closeSearch} style={{ position: "absolute", inset: 0, background: "rgba(10, 9, 15, .28)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: "min(760px, calc(100vw - 32px))", maxHeight: "min(56vh, 680px)", overflow: "hidden", borderRadius: "28px", border: "1px solid var(--line)", background: "color-mix(in srgb, var(--paper) 95%, transparent)", boxShadow: "0 24px 80px rgba(0,0,0,.18)", backdropFilter: "saturate(180%) blur(20px)" }}>
            <form onSubmit={submitSearch} style={{ padding: 18, borderBottom: "1px solid var(--line)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 64, padding: "0 14px 0 18px", borderRadius: 22, background: "var(--surface-2)", border: "1px solid var(--line-2)" }}>
                <Icon name="search" size={22} style={{ color: "var(--ink-4)", flex: "none" }} />
                <input
                  ref={searchInputRef}
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder={t("search_ph")}
                  style={{ flex: 1, minWidth: 0, border: "none", outline: "none", boxShadow: "none", WebkitAppearance: "none", background: "transparent", color: "var(--ink)", fontSize: 20, fontWeight: 600, fontFamily: "inherit" }}
                />
                <button type="button" className="btn-icon btn-ghost" onClick={clearSearchInput} aria-label={t("reset")} style={{ flex: "0 0 auto", width: 36, height: 36, minWidth: 36, padding: 0 }}>
                  <Icon name="x" size={18} />
                </button>
              </div>
            </form>
            <div style={{ maxHeight: "calc(min(56vh, 680px) - 102px)", overflowY: "auto", padding: filteredResults.length ? "8px" : "24px 22px 28px" }}>
              {!debouncedSearch ? (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-3)" }}>{t("search_ph")}</div>
                  <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>{t("search_hint")}</p>
                </div>
              ) : filteredResults.length ? (
                filteredResults.map((item) => {
                  const authorText = displayUserName(item.author, lang);
                  return (
                    <button key={item.id} onClick={() => selectSearchResult(item)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: 12, borderRadius: 20, textAlign: "left", background: "transparent", border: "none", transition: "background .15s var(--ease)" }}>
                      <div style={{ width: 88, flex: "0 0 88px" }}>
                        <RemoteImage item={item} ratio="1 / 1" radius="16px" loadingPlaceholder="skeleton" />
                      </div>
                      <div style={{ minWidth: 0, flex: 1, display: "grid", gap: 6 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{authorText}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {(item.tags || []).slice(0, 3).map((tg) => (
                            <span key={tg} style={{ height: 24, padding: "0 10px", borderRadius: 999, display: "inline-flex", alignItems: "center", background: "var(--surface-2)", color: "var(--ink-3)", fontSize: 12, fontWeight: 700 }}>#{lang === "en" ? (TAG_EN[tg] || tg) : tg}</span>
                          ))}
                        </div>
                      </div>
                      <Icon name="chevronRight" size={20} style={{ color: "var(--ink-4)", flex: "none" }} />
                    </button>
                  );
                })
              ) : (
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{t("empty_title")}</div>
                  <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>{t("empty_desc")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* mobile drawer */}
      {menuOpen && (
        <div className="mobile-drawer-layer" style={{ position: "fixed", inset: 0, zIndex: 120 }}>
          <div className="mobile-drawer-backdrop" onClick={() => setMenuOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(22,21,27,.5)" }} />
          <div className="mobile-drawer-panel" style={{
            position: "absolute", right: 0, top: 0, bottom: 0, width: "100%", maxWidth: "none",
            background: "var(--paper)", padding: 18, display: "flex", flexDirection: "column", gap: 6,
            animation: "slideIn .25s var(--ease-out)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <Logo size={20} theme={theme} onClick={() => { setMenuOpen(false); go("gallery"); }} />
              <button className="btn-icon btn-ghost" onClick={() => setMenuOpen(false)} aria-label={t("close")}><Icon name="x" /></button>
            </div>
            {nav.map((n) => (
              <button key={n.id} onClick={() => { setMenuOpen(false); go(n.id); }} style={{
                ...menuItemStyle, height: 48, fontSize: 16, fontWeight: 700,
                color: isActive(n.id) ? "var(--accent)" : "var(--ink)",
              }}>{n.label}</button>
            ))}
            <hr className="divider" style={{ margin: "8px 0" }} />
            {CONFIG.FEATURES.generation && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button className="btn btn-spark" onClick={() => { setMenuOpen(false); go("generate"); }}>
                  <Icon name="wand" size={18} stroke={2} /> {t("g_cta")}
                </button>
                <button className="btn btn-spark" onClick={() => { setMenuOpen(false); go("upload"); }}>
                  <Icon name="image" size={18} stroke={2} /> {t("g_upload")}
                </button>
              </div>
            )}
            {auth ? (
              <button className="btn btn-line" onClick={() => { setMenuOpen(false); onLogout(); }} style={{ marginTop: 4 }}>{t("logout")}</button>
            ) : (
              <button className="btn btn-line" onClick={() => { setMenuOpen(false); onLogin(); }} style={{ marginTop: 4 }}>{t("login")}</button>
            )}
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-start" }}>
              {themeToggle("")}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const menuItemStyle = {
  display: "flex", alignItems: "center", gap: 10, width: "100%",
  height: 40, padding: "0 10px", borderRadius: "var(--r-sm)",
  fontSize: 14, fontWeight: 600, color: "var(--ink)", textAlign: "left",
  transition: "background .12s var(--ease)",
};

function normalizeSearchQuery(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
}

function splitSearchTerms(value) {
  return normalizeSearchQuery(value)
    .toLowerCase()
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/* ---------- Image card (gallery) ---------- */
function ImageCard({ item, onOpen, liked, onLike, layout = "uniform", index = 0, reveal = true }) {
  const { lang, t } = useT();
  const [hover, setHover] = React.useState(false);
  const ratio = layout === "masonry" ? item.ratio : (layout === "uniform" ? "1 / 1" : "4 / 3");
  const activate = () => onOpen(item);
  const nm = authorName(item.author, lang);
  const displayName = nm.length > 15 ? `${nm.slice(0, 15)}...` : nm;
  const promptText = lang === "en" && item.promptEn ? item.promptEn : item.prompt;
  const card = (
    <figure
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onClick={activate}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } }}
      aria-label={promptText.slice(0, 48)}
      style={{
        margin: 0, cursor: "pointer", position: "relative", breakInside: "avoid",
        borderRadius: "var(--r-md)", overflow: "hidden", background: "var(--surface-3)",
        boxShadow: hover ? "var(--sh-3)" : "var(--sh-1)",
        transform: hover ? "translateY(-4px)" : "none",
        transition: "box-shadow .22s var(--ease), transform .22s var(--ease-out)",
      }}
    >
      <RemoteImage item={item} ratio={ratio} radius="0" loadingPlaceholder="skeleton"
        style={{ transform: hover ? "scale(1.045)" : "none", transition: "transform .5s var(--ease-out)" }}>
        {/* hover overlay */}
        <div style={{
          position: "absolute", inset: 0, padding: 18,
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
          background: "linear-gradient(0deg, rgba(10,9,15,.78) 0%, rgba(10,9,15,.15) 42%, rgba(10,9,15,0) 70%)",
          opacity: hover ? 1 : 0, transition: "opacity .2s var(--ease)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <Avatar author={item.author} size={26} />
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 600, minWidth: 0, flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
            <span style={{ marginLeft: "auto", flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 4,
              color: "rgba(255,255,255,.92)", fontSize: 13, fontWeight: 600 }}>
              <Icon name="heart" size={15} /> <span className="tnum">{item.likes.toLocaleString()}</span>
            </span>
          </div>
        </div>
        {/* top-right actions */}
        {CONFIG.FEATURES.social && <div style={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 6,
          opacity: hover || liked ? 1 : 0, transition: "opacity .2s var(--ease)" }}>
          <button onClick={(e) => { e.stopPropagation(); onLike(item.id); }} aria-label={t("like")}
            style={iconBtnGlass}>
            {liked ? <HeartSolid size={17} style={{ color: "var(--like)" }} /> : <Icon name="heart" size={17} style={{ color: "#fff" }} />}
          </button>
        </div>}
        {/* model chip */}
        <div style={{ position: "absolute", top: 14, left: 14, opacity: hover ? 1 : 0,
          transition: "opacity .2s var(--ease)" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5, height: 26, padding: "0 10px",
            borderRadius: "var(--r-pill)", background: "rgba(22,21,27,.55)", backdropFilter: "blur(6px)",
            color: "#fff", fontSize: 12, fontWeight: 700,
          }}>
            <Icon name="sparkles" size={13} stroke={2} /> {item.model}
          </span>
        </div>
      </RemoteImage>
    </figure>
  );

  return reveal ? (
    <Reveal delay={(index % 12) * 30} className="card-reveal">
      {card}
    </Reveal>
  ) : (
    <div className="card-reveal">{card}</div>
  );
}

const iconBtnGlass = {
  width: 34, height: 34, borderRadius: "50%", display: "inline-flex",
  alignItems: "center", justifyContent: "center",
  background: "rgba(22,21,27,.45)", backdropFilter: "blur(6px)",
  transition: "background .15s var(--ease)",
};

function LoginGate({ onLogin, go, title, desc, primaryLabel, secondaryLabel, icon = "lock", primaryIcon = "sparkles", features }) {
  const { lang } = useT();
  const featureItems = features === false ? [] : (features || GATE_FEATURES[lang] || GATE_FEATURES.ko);
  return (
    <div className="container" style={{ padding: "clamp(40px,6vw,80px) 24px", display: "flex", justifyContent: "center" }}>
      <div className="card" style={{ maxWidth: 460, width: "100%", padding: "clamp(28px,4vw,44px)", borderRadius: "var(--r-xl)",
        textAlign: "center", boxShadow: "var(--sh-2)" }}>
        <div style={{ width: 64, height: 64, borderRadius: 18, background: "var(--spark)", margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "var(--sh-yellow)" }}>
          <Icon name={icon} size={30} stroke={2} style={{ color: "#000" }} />
        </div>
        <h1 style={{ fontSize: 26 }}>{title}</h1>
        <p className="muted" style={{ marginTop: 12, fontSize: 15.5, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: desc }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 28 }}>
          <button className="btn btn-spark btn-lg" onClick={onLogin}>
            {primaryIcon && <Icon name={primaryIcon} size={18} stroke={2} />} {primaryLabel}
          </button>
          {secondaryLabel && <button className="btn btn-ghost" onClick={() => go("gallery")}>{secondaryLabel}</button>}
        </div>
        {featureItems.length > 0 && (
          <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 26, flexWrap: "wrap" }}>
            {featureItems.map(([ic, lbl]) => (
              <span key={lbl} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-3)" }}>
                <Icon name={ic} size={15} style={{ color: "var(--accent)" }} /> {lbl}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

Object.assign(window, { Reveal, Logo, Avatar, authorName, displayUserName, RemoteImage, Header, ImageCard: React.memo(ImageCard), LoginGate, menuItemStyle, iconBtnGlass });
